import React, { useState, useEffect, useMemo, useContext, useRef } from "react";
import StarekContext from "../../context/StarekContext";
import { printReports } from "../../utils";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

function IconButton({ children, title, onClick, disabled }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-slate-300 tw-bg-white tw-text-xs tw-text-slate-700 hover:tw-bg-slate-50 ${
        disabled ? "tw-opacity-50 tw-cursor-not-allowed" : ""
      }`}
    >
      {children}
    </button>
  );
}

const SIGNALS_TO_PLOT = [
  { key: "/node0/port0", name: "/node0/port0", type: "analog" },
  { key: "/node0/port1", name: "/node0/port1", type: "digital" },
  { key: "/node0/pwr", name: "/node0/pwr", type: "analog" },
  { key: "/node0/wifi_rssi", name: "/node0/wifi_rssi", type: "analog" },
];

// --- RGB HEX → RGB ---
function hexToRgb(hex) {
  hex = hex.replace('#','');
  return [
    parseInt(hex.substring(0,2),16),
    parseInt(hex.substring(2,4),16),
    parseInt(hex.substring(4,6),16)
  ];
}

// --- RGB → HSL ---
function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = d / (1 - Math.abs(2*l - 1));
    switch (max) {
      case r: h = 60 * (((g - b) / d) % 6); break;
      case g: h = 60 * ((b - r) / d + 2); break;
      case b: h = 60 * ((r - g) / d + 4); break;
    }
  }
  if (h < 0) h += 360;
  return { h, s, l };
}

// --- HSL → RGB ---
function hslToRgb(h, s, l) {
  const c = (1 - Math.abs(2*l - 1)) * s;
  const x = c * (1 - Math.abs((h/60)%2 - 1));
  const m = l - c/2;

  let r, g, b;
  if (h < 60) [r,g,b] = [c,x,0];
  else if (h < 120) [r,g,b] = [x,c,0];
  else if (h < 180) [r,g,b] = [0,c,x];
  else if (h < 240) [r,g,b] = [0,x,c];
  else if (h < 300) [r,g,b] = [x,0,c];
  else [r,g,b] = [c,0,x];

  return {
    r: Math.round((r+m)*255),
    g: Math.round((g+m)*255),
    b: Math.round((b+m)*255)
  };
}

function rgbToHex(r,g,b) {
  return "#" + [r,g,b].map(x =>
    x.toString(16).padStart(2,'0')
  ).join('');
}

function getColorVariant(baseColor, percentage) {
    if (!baseColor) return "#000000";
    
    const hex = baseColor.startsWith('#') ? baseColor : '#' + baseColor;
    const [r, g, b] = hexToRgb(hex);
    
    let alpha = percentage / 25;
    if (alpha > 1) alpha = 1;
    if (alpha < 0.1 && percentage > 0) alpha = 0.1;
    
    return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(2)})`;
}

const BUCKET_COLORS = {
  off: "#ef4444",
  on: "#22c55e",
};

export default function EfficiencyReport({ filters, range, fetchRawData, onRefresh, onBarClick, deviceSignalColors, onFavoriteClick, onDownloadCSV }) {
  const { types } = useContext(StarekContext);
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentDeviceIndex, setCurrentDeviceIndex] = useState(0);
  const printRef = useRef(null);

  const defaultSignalColors = types?.device?.defaultSignalColors || [];
  
  const handleDownloadCSV = () => {
    if (onDownloadCSV) onDownloadCSV();
  };

  const loadData = async () => {
    if (onRefresh) {
      const didUpdate = await onRefresh();
      if (didUpdate) return;
    }

    if (fetchRawData) {
      setLoading(true);
      fetchRawData()
        .then((data) => {
          if (data && data.success) {
            setRawData(data.data);
            setCurrentDeviceIndex(0);
          } else {
            setRawData(null);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch raw data", err);
          setRawData(null);
        })
        .finally(() => setLoading(false));
    }
  };

  useEffect(() => {
    if (fetchRawData) {
        setLoading(true);
        fetchRawData()
            .then((data) => {
            if (data && data.success) {
                setRawData(data.data);
                setCurrentDeviceIndex(0);
            } else {
                setRawData(null);
            }
            })
            .catch((err) => {
            console.error("Failed to fetch raw data", err);
            setRawData(null);
            })
            .finally(() => setLoading(false));
    }
  }, [filters, range, fetchRawData]);

  const deviceList = useMemo(() => {
    if (filters?.devices) {
      return filters.devices.split(',').map(d => d.trim()).filter(Boolean);
    }
    return rawData ? Object.keys(rawData) : [];
  }, [rawData, filters]);

  const currentDeviceName = deviceList[currentDeviceIndex];

  const chartData = useMemo(() => {
    if (!currentDeviceName || !rawData || !rawData[currentDeviceName]) return [];

    const deviceSignals = rawData[currentDeviceName];
    const deviceColors = deviceSignalColors?.[currentDeviceName] || {};
    
    return SIGNALS_TO_PLOT.map((signal, idx) => {
        const points = deviceSignals[signal.key] || [];
        if (points.length === 0) return null;

        let totalPoints = points.length;
        
        let buckets = {
            off: 0,
            on: 0,
            b10: 0, b20: 0, b30: 0, b40: 0, b50: 0,
            b60: 0, b70: 0, b80: 0, b90: 0, b100: 0
        };

        points.forEach(p => {
            const val = p.measure_value;
            if (signal.type === 'digital') {
                if (val > 0) buckets.on++;
                else buckets.off++;
            } else {
                const pct = (val / 1023) * 100;
                if (pct <= 10) buckets.b10++;
                else if (pct <= 20) buckets.b20++;
                else if (pct <= 30) buckets.b30++;
                else if (pct <= 40) buckets.b40++;
                else if (pct <= 50) buckets.b50++;
                else if (pct <= 60) buckets.b60++;
                else if (pct <= 70) buckets.b70++;
                else if (pct <= 80) buckets.b80++;
                else if (pct <= 90) buckets.b90++;
                else buckets.b100++;
            }
        });

        const result = { 
            name: signal.name, 
            type: signal.type,
            baseColor: deviceColors[signal.key] || defaultSignalColors[idx % defaultSignalColors.length] || "#333333"
        };

        Object.keys(buckets).forEach(key => {
            if (totalPoints > 0) {
                result[key] = (buckets[key] / totalPoints) * 100;
            } else {
                result[key] = 0;
            }
        });

        return result;
    }).filter(Boolean);
  }, [rawData, currentDeviceName, defaultSignalColors, deviceSignalColors, deviceList, currentDeviceIndex]);

  const handlePrevDevice = () => {
    setCurrentDeviceIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const handleNextDevice = () => {
    setCurrentDeviceIndex((prev) =>
      prev < deviceList.length - 1 ? prev + 1 : prev
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        const isDigital = data.type === 'digital';

        return (
            <div className="tw-bg-white tw-p-2 tw-border tw-border-slate-200 tw-shadow-md tw-rounded tw-text-xs">
                <p className="tw-font-semibold tw-mb-1">{label}</p>
                {isDigital ? (
                    <>
                        <div style={{ color: getColorVariant(data.baseColor, 100) }}>On: {data.on.toFixed(1)}%</div>
                        <div style={{ color: getColorVariant(data.baseColor, 30) }}>Off: {data.off.toFixed(1)}%</div>
                    </>
                ) : (
                    <>
                        {[100, 90, 80, 70, 60, 50, 40, 30, 20, 10].map(pct => {
                            const key = `b${pct}`;
                            const endVal = Math.round((pct / 100) * 1023);
                            const startVal = Math.round(((pct - 10) / 100) * 1023);
                            const range = `${startVal}-${endVal}`;
                            const val = data[key];
                            const color = getColorVariant(data.baseColor, val);
                            if (val === 0) return null;
                            return (
                                <div key={key} style={{ color }}>
                                    {range}: {val.toFixed(1)}%
                                </div>
                            );
                        })}
                    </>
                )}
            </div>
        );
    }
    return null;
  };

  return (
    <div className="tw-flex tw-flex-col tw-h-full tw-gap-4" ref={printRef}>
      <div className="tw-flex tw-items-center tw-justify-between tw-relative">
        <div className="tw-flex tw-items-center tw-gap-2 tw-bg-white tw-border tw-border-slate-200 tw-rounded-md tw-px-2 tw-py-1">
            {deviceList.length > 0 ? (
                <>
                    <button 
                        onClick={handlePrevDevice} 
                        disabled={currentDeviceIndex === 0}
                        className="tw-p-1 hover:tw-bg-slate-100 tw-rounded disabled:tw-opacity-30"
                    >
                        <ChevronLeftIcon fontSize="small" />
                    </button>
                    <span className="tw-text-sm tw-font-medium tw-min-w-[150px] tw-text-center">
                        {currentDeviceName}
                    </span>
                    <button 
                        onClick={handleNextDevice} 
                        disabled={currentDeviceIndex === deviceList.length - 1}
                        className="tw-p-1 hover:tw-bg-slate-100 tw-rounded disabled:tw-opacity-30"
                    >
                        <ChevronRightIcon fontSize="small" />
                    </button>
                </>
            ) : (
                <span className="tw-text-sm tw-text-slate-500 tw-px-2">No devices</span>
            )}
        </div>

        <div className="tw-flex tw-items-center tw-gap-2">
            <IconButton title="Refresh Data" onClick={loadData}>
                 <RefreshIcon fontSize="small" className={loading ? "tw-animate-spin" : ""} />
            </IconButton>

          <IconButton title="Favourite" onClick={() => {
            console.log("Favorite Clicked in EfficiencyReport");
            if (onFavoriteClick) onFavoriteClick();
          }}>
            <FavoriteBorderIcon fontSize="small" />
          </IconButton>

          <IconButton title="Print chart" onClick={() => printReports(printRef.current)}>
            <PrintIcon fontSize="small" />
          </IconButton>

          <IconButton title="Download CSV" onClick={handleDownloadCSV}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      <div className="tw-flex-1 tw-rounded-lg tw-bg-white tw-border tw-border-slate-200 tw-p-4 tw-flex tw-items-center tw-justify-center tw-min-h-[400px]">
        {loading ? (
            <div className="tw-text-slate-500">Loading...</div>
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5,
              }}
              onClick={(data) => {
                if (data && data.activePayload && data.activePayload.length > 0) {
                    const payload = data.activePayload[0].payload;
                    onBarClick?.({
                        device: currentDeviceName,
                        signal: payload.name 
                    });
                }
              }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e0e0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis 
                domain={[0, 100]} 
                tickFormatter={(val) => `${val}%`}
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                yAxisId="right" 
                orientation="right" 
                domain={[0, 100]} 
                tick={{ fontSize: 12 }}
                tickFormatter={(val) => val === 100 ? 'On' : (val === 0 ? 'Off' : '')}
                axisLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              
              {/* Digital Bars */}
              <Bar dataKey="off" stackId="a" name="Off">
                {chartData.map((entry, index) => (
                    <Cell key={`cell-off-${index}`} fill={getColorVariant(entry.baseColor, 30)} />
                ))}
              </Bar>
              <Bar dataKey="on" stackId="a" name="On">
                {chartData.map((entry, index) => (
                    <Cell key={`cell-on-${index}`} fill={entry.baseColor} />
                ))}
              </Bar>

              {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(pct => (
                  <Bar 
                    key={`b${pct}`} 
                    dataKey={`b${pct}`} 
                    stackId="a" 
                    name={pct === 100 ? "Analog" : undefined} 
                    legendType={pct === 100 ? 'rect' : 'none'}
                  >
                    {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getColorVariant(entry.baseColor, entry[`b${pct}`])} />
                    ))}
                  </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <span className="tw-text-xs tw-text-slate-400">
            {loading ? "Loading..." : "No data available for selected device"}
          </span>
        )}
      </div>
    </div>
  );
}
